import { FC, useContext } from "react";
import { useRouter } from "next/router";
import { Trans } from "@lingui/macro";
import { AppCtx } from "src/contexts/app.context";
import { LogoSquare } from "src/icons/LogoSquare";
import { useSmartBanner } from "src/hooks/useSmartBanner";
import { localeDomainMap } from "src/utils/domains";
import { useBreakpoints } from "@otrium/core";
import {
  StyledSmartBanner,
  StyledSmartBannerClose,
  StyledSmartBannerContent,
  StyledSmartBannerLogoWrapper,
  StyledSmartBannerTextContainer,
  StyledSmartBannerTitle,
  StyledSmartBannerDescription,
  StyledSmartBannerLink,
} from "./SmartBanner.styled";
import { getUrlParams } from "src/modules/page/utils";
import { useTheme, Theme } from "@emotion/react";
import getConfig from "next/config";

const { publicRuntimeConfig } = getConfig();

const SmartBanner: FC = () => {
  const { locale } = useContext(AppCtx);
  const router = useRouter();
  const [showSmartBanner, handleHideSmartBanner] = useSmartBanner();
  const theme: Theme = useTheme();

  const { isMobile } = useBreakpoints();

  if (!showSmartBanner || !isMobile) {
    return null;
  }

  const isProd = process.env.RELEASE_STAGE === "production";

  const {
    domainWithoutLocale,
    ios: { ibi },
    android: { apn },
  } = isProd
    ? getUrlParams(publicRuntimeConfig.LOCALE || locale).production
    : getUrlParams(publicRuntimeConfig.LOCALE || locale).staging;

  const localeDomain =
    localeDomainMap[publicRuntimeConfig.LOCALE || locale] || localeDomainMap.en;

  const encodedPath = encodeURIComponent(router.asPath);

  return (
    <StyledSmartBanner>
      <StyledSmartBannerContent>
        <StyledSmartBannerClose
          onClick={handleHideSmartBanner}
          fill={theme.colors.tone.anthraciteLight}
        />

        <StyledSmartBannerLogoWrapper>
          <LogoSquare />
        </StyledSmartBannerLogoWrapper>

        <StyledSmartBannerTextContainer>
          <StyledSmartBannerTitle>
            <Trans>The Otrium App</Trans>
          </StyledSmartBannerTitle>

          <StyledSmartBannerDescription>
            <Trans>Free in the app store</Trans>
          </StyledSmartBannerDescription>
        </StyledSmartBannerTextContainer>
      </StyledSmartBannerContent>

      <StyledSmartBannerLink
        href={`https://app.otrium.com/?link=${domainWithoutLocale}${localeDomain}${encodedPath}&apn=${apn}&efr=true&ibi=${ibi}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Trans>Open</Trans>
      </StyledSmartBannerLink>
    </StyledSmartBanner>
  );
};

export default SmartBanner;
